import { watch } from 'vue'
import { liveKitManager } from './manager'
import { createMicLevelMeter } from './micLevelMeter'
import { debugPublisher } from '~/lib/debug/debugPublisher'

/**
 * LiveKit participant attribute key other clients can read off `participant.attributes` to
 * know "is this person currently speaking", per https://docs.livekit.io/home/client/data/participant-attributes/ —
 * `LocalParticipant.setAttributes()` merges/broadcasts a partial key/value update to everyone
 * in the room and fires `RoomEvent.ParticipantAttributesChanged` for them. Requires the
 * `canUpdateOwnMetadata` grant on the LiveKit access token (minted server-side, outside this
 * repo) — `setAttributes` rejects silently-logged if that grant is missing.
 */
export const SPEAKING_ATTRIBUTE_KEY = 'speaking'

// The 0..1 analyser-level threshold above which the mic counts as "speaking" is user-configurable
// (see `liveKitManager.state.micSensitivity` / the "Чувствительность микрофона" slider in
// `CallControls.vue`) rather than a fixed constant here — true silence reads as an exact 0 (every
// sample sits at the ADC midpoint), but real-world ambient noise almost never does, and how much
// of that noise floor counts as "speaking" depends heavily on mic/room/gain, hence configurable.
// Report "started speaking" the instant the threshold is crossed (that's the whole point of
// driving this off the zero-latency analyser instead of LiveKit's own polled
// `ActiveSpeakersChanged`), but hold off reporting "stopped" for a moment — otherwise the
// natural gaps between words/sentences would flap the attribute, and the network call behind
// it, on and off many times a second.
const STOP_HANGOVER_MS = 400
// Hard floor on how often `setAttributes` is actually called, independent of the hangover
// above — the throttle the user asked for, so a pathological flicker right at the threshold
// can't turn into a request storm.
const MIN_SEND_INTERVAL_MS = 250

class SpeakingAttributePublisher {
  private started = false
  private readonly meter = createMicLevelMeter()
  private sentValue: boolean | null = null
  private lastSentAt = 0
  private stopTimer: ReturnType<typeof setTimeout> | null = null
  private pendingSendTimer: ReturnType<typeof setTimeout> | null = null

  start() {
    if (this.started) return
    this.started = true

    // A new room means a brand-new server-side participant object — whatever we last sent to
    // the previous one doesn't carry over, so the local "already sent" cache must not either,
    // or the first `setAttributes` call after a reconnect gets skipped as a no-op duplicate.
    watch(() => liveKitManager.state.room, () => this.reset())
    watch(this.meter.level, (level) => this.onLevel(level))
  }

  private reset() {
    if (this.stopTimer) clearTimeout(this.stopTimer)
    if (this.pendingSendTimer) clearTimeout(this.pendingSendTimer)
    this.stopTimer = null
    this.pendingSendTimer = null
    this.sentValue = null
    this.lastSentAt = 0
  }

  private onLevel(level: number) {
    const speaking = level > liveKitManager.state.micSensitivity
    if (speaking) {
      if (this.stopTimer) {
        clearTimeout(this.stopTimer)
        this.stopTimer = null
      }
      this.request(true)
    } else if (this.sentValue !== false && !this.stopTimer) {
      this.stopTimer = setTimeout(() => {
        this.stopTimer = null
        this.request(false)
      }, STOP_HANGOVER_MS)
    }
  }

  private request(value: boolean) {
    if (this.sentValue === value) return
    const elapsed = Date.now() - this.lastSentAt
    if (elapsed >= MIN_SEND_INTERVAL_MS) {
      this.send(value)
    } else if (!this.pendingSendTimer) {
      this.pendingSendTimer = setTimeout(() => {
        this.pendingSendTimer = null
        this.send(value)
      }, MIN_SEND_INTERVAL_MS - elapsed)
    }
  }

  private send(value: boolean) {
    const participant = liveKitManager.room?.localParticipant
    if (!participant) {
      debugPublisher.log('livekit', 'warn', `setAttributes(speaking=${value}) пропущен — нет активного localParticipant`)
      return
    }
    this.sentValue = value
    this.lastSentAt = Date.now()
    debugPublisher.log('livekit', 'info', `-> setAttributes(speaking=${value})`)
    participant.setAttributes({ [SPEAKING_ATTRIBUTE_KEY]: String(value) }).then(() => {
      debugPublisher.log('livekit', 'info', `<- setAttributes(speaking=${value}) подтверждён сервером`)
    }).catch(error => {
      // The most common cause: the LiveKit access token (minted server-side) doesn't grant
      // `canUpdateOwnMetadata` — this rejects (often after ~5s, `requestMetadataUpdate`'s own
      // timeout) rather than failing fast, so don't mistake a delayed rejection for a hang.
      console.warn('[speakingAttributePublisher] Failed to update speaking attribute', error)
      debugPublisher.log('livekit', 'error', `setAttributes(speaking=${value}) отклонён: ${error?.message ?? error}`)
    })
  }
}

export const speakingAttributePublisher = new SpeakingAttributePublisher()
